import { persistIntentProfile } from "./storage";
import { AcquisitionContext, IntentUpdate, ShopperContext } from "./types";

interface IntentRule {
  source: string;
  signal: string;
  intent: string;
  delta: number;
  confidence: number;
}

export function applyIntentSignals(
  context: ShopperContext,
  eventName: string,
  properties: Record<string, unknown>,
  now: string,
): IntentUpdate[] {
  const rules = getIntentRules(eventName, properties);
  if (!rules.length) return [];

  const updates = rules.map((rule) => {
    const currentScore = context.intentProfile.scores[rule.intent] ?? 0;
    const score = clamp(currentScore + rule.delta);
    context.intentProfile.scores[rule.intent] = score;

    return {
      ...rule,
      score,
    };
  });

  context.intentProfile.topIntent = getTopIntent(context.intentProfile.scores);
  context.intentProfile.updatedAt = now;
  persistIntentProfile(context.intentProfile);
  return updates;
}

function getIntentRules(eventName: string, properties: Record<string, unknown>): IntentRule[] {
  if (eventName === "gemsales.sdk.loaded") {
    return acquisitionIntentRules(properties.acquisition);
  }

  if (eventName === "behavior.faq_opened") {
    return faqIntentRules(String(properties.faq_intent ?? ""));
  }

  if (eventName === "behavior.cart_idle") {
    return [
      rule("behavior", "cart_idle", "free_shipping_opportunity", 0.45, 0.8),
      rule("behavior", "cart_idle", "price_value", 0.2, 0.55),
    ];
  }

  if (eventName === "behavior.cta_clicked") {
    return [
      rule("behavior", "cta_clicked", "commercial_intent", 0.25, 0.7),
      rule("behavior", "cta_clicked", "product_fit", 0.15, 0.5),
    ];
  }

  if (eventName === "behavior.search_focused") {
    return [rule("behavior", "search_focused", "product_discovery", 0.2, 0.45)];
  }

  if (eventName === "behavior.scroll_depth") {
    const depth = Number(properties.depth_percent ?? 0);
    if (depth >= 90) return [rule("behavior", "scroll_depth_90", "unresolved_objection", 0.15, 0.45)];
    if (depth >= 75) return [rule("behavior", "scroll_depth_75", "unresolved_objection", 0.1, 0.35)];
  }

  if (eventName === "gemsales.quick_reply.click") {
    return quickReplyIntentRules(String(properties.quick_reply ?? ""));
  }

  if (eventName === "gemsales.flow.answer") {
    return answerIntentRules(String(properties.answer_key ?? ""));
  }

  if (eventName === "gemsales.followup.submit") {
    return followUpIntentRules(String(properties.answer_topic ?? ""), String(properties.question ?? ""));
  }

  return [];
}

function acquisitionIntentRules(value: unknown): IntentRule[] {
  if (!isAcquisitionContext(value)) return [];

  const firstTouch = value.firstTouch;
  const text = [
    value.source,
    value.medium,
    value.campaign,
    value.content,
    value.term,
    value.referrer,
    value.landingPage,
    firstTouch?.source,
    firstTouch?.medium,
    firstTouch?.campaign,
    firstTouch?.content,
    firstTouch?.term,
    firstTouch?.referrer,
    firstTouch?.landingPage,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
  const rules: IntentRule[] = [];

  if (includesAny(text, ["fake", "authentic", "real", "contamination", "heavy", "metal", "lab", "coa", "proof"])) {
    rules.push(rule("acquisition", "traffic.proof_trust", "lab_proof", 0.45, 0.75));
    rules.push(rule("acquisition", "traffic.proof_trust", "trust_safety", 0.4, 0.75));
  }

  if (includesAny(text, ["resin", "gummies", "compare", "vs", "format"])) {
    rules.push(rule("acquisition", "traffic.format_compare", "format_choice", 0.35, 0.65));
    rules.push(rule("acquisition", "traffic.format_compare", "product_fit", 0.25, 0.55));
  }

  if (includesAny(text, ["energy", "focus", "vitality"])) {
    rules.push(rule("acquisition", "traffic.energy_focus", "energy_focus", 0.35, 0.65));
  }

  if (includesAny(text, ["recovery", "workout", "creatine", "strength"])) {
    rules.push(rule("acquisition", "traffic.recovery", "recovery", 0.35, 0.65));
  }

  if (includesAny(text, ["price", "discount", "deal", "value", "worth", "free shipping"])) {
    rules.push(rule("acquisition", "traffic.value", "price_value", 0.35, 0.65));
  }

  if (includesAny(text, ["women", "menopause", "hormone"])) {
    rules.push(rule("acquisition", "traffic.women_wellness", "women_wellness", 0.35, 0.65));
  }

  if (includesAny(text, ["meta", "facebook", "instagram", "tiktok", "presale", "story", "advertorial"])) {
    rules.push(rule("acquisition", "traffic.discovery", "product_discovery", 0.2, 0.45));
  }

  return dedupeRules(rules);
}

function faqIntentRules(faqIntent: string): IntentRule[] {
  const rules: Record<string, IntentRule[]> = {
    medication_condition: [rule("behavior", "faq.medication_condition", "medication_condition", 0.7, 0.9)],
    lab_proof: [
      rule("behavior", "faq.lab_proof", "lab_proof", 0.5, 0.85),
      rule("behavior", "faq.lab_proof", "trust_safety", 0.35, 0.75),
    ],
    taste: [rule("behavior", "faq.taste", "taste_concern", 0.45, 0.75)],
    shipping_returns: [rule("behavior", "faq.shipping_returns", "shipping_returns", 0.45, 0.7)],
    unresolved_objection: [rule("behavior", "faq.unresolved_objection", "unresolved_objection", 0.3, 0.55)],
  };

  return rules[faqIntent] ?? rules.unresolved_objection ?? [];
}

function quickReplyIntentRules(quickReply: string): IntentRule[] {
  const normalized = quickReply.toLowerCase();
  if (normalized.includes("energy") || normalized.includes("focus")) {
    return [rule("explicit", "quick_reply.energy_focus", "energy_focus", 0.45, 0.8)];
  }
  if (normalized.includes("recovery") || normalized.includes("workout")) {
    return [rule("explicit", "quick_reply.recovery", "recovery", 0.45, 0.8)];
  }
  if (normalized.includes("proof") || normalized.includes("lab")) {
    return [rule("explicit", "quick_reply.proof", "lab_proof", 0.55, 0.85)];
  }
  if (normalized.includes("taste") || normalized.includes("gummies") || normalized.includes("resin")) {
    return [rule("explicit", "quick_reply.format_choice", "format_choice", 0.55, 0.85)];
  }
  if (normalized.includes("women")) {
    return [rule("explicit", "quick_reply.women_wellness", "women_wellness", 0.55, 0.8)];
  }

  return [rule("explicit", "quick_reply.general", "product_fit", 0.25, 0.5)];
}

function answerIntentRules(answerKey: string): IntentRule[] {
  const normalized = answerKey.toLowerCase();
  if (normalized.includes("medication") || normalized.includes("safe")) {
    return [rule("explicit", `answer.${answerKey}`, "medication_condition", 0.7, 0.9)];
  }
  if (normalized.includes("proof") || normalized.includes("real") || normalized.includes("fake") || normalized.includes("coa") || normalized.includes("heavy")) {
    return [
      rule("explicit", `answer.${answerKey}`, "lab_proof", 0.55, 0.85),
      rule("explicit", `answer.${answerKey}`, "trust_safety", 0.3, 0.75),
    ];
  }
  if (normalized.includes("taste") || normalized.includes("easy") || normalized.includes("convenience")) {
    return [rule("explicit", `answer.${answerKey}`, "format_choice", 0.45, 0.75)];
  }
  if (normalized.includes("price") || normalized.includes("value")) {
    return [rule("explicit", `answer.${answerKey}`, "price_value", 0.45, 0.75)];
  }
  if (normalized.includes("energy") || normalized.includes("focus")) {
    return [rule("explicit", `answer.${answerKey}`, "energy_focus", 0.5, 0.8)];
  }
  if (normalized.includes("recovery")) {
    return [rule("explicit", `answer.${answerKey}`, "recovery", 0.5, 0.8)];
  }

  return [rule("explicit", `answer.${answerKey}`, "product_fit", 0.25, 0.5)];
}

function followUpIntentRules(answerTopic: string, question: string): IntentRule[] {
  const normalized = `${answerTopic} ${question}`.toLowerCase();
  if (normalized.includes("medication")) {
    return [rule("explicit", "followup.medication", "medication_condition", 0.65, 0.9)];
  }
  if (normalized.includes("heavy") || normalized.includes("coa") || normalized.includes("fulvic") || normalized.includes("proof")) {
    return [
      rule("explicit", `followup.${answerTopic}`, "lab_proof", 0.55, 0.85),
      rule("explicit", `followup.${answerTopic}`, "trust_safety", 0.35, 0.75),
    ];
  }
  if (normalized.includes("taste") || normalized.includes("resin") || normalized.includes("gummies")) {
    return [rule("explicit", "followup.taste", "format_choice", 0.45, 0.75)];
  }
  if (normalized.includes("value") || normalized.includes("price") || normalized.includes("cost")) {
    return [rule("explicit", "followup.value", "price_value", 0.45, 0.75)];
  }

  return [rule("explicit", "followup.unknown", "unresolved_objection", 0.3, 0.55)];
}

function isAcquisitionContext(value: unknown): value is AcquisitionContext {
  return Boolean(value && typeof value === "object" && "landingPage" in value);
}

function includesAny(value: string, keywords: string[]): boolean {
  return keywords.some((keyword) => value.includes(keyword));
}

function dedupeRules(rules: IntentRule[]): IntentRule[] {
  const seen = new Set<string>();
  return rules.filter((candidate) => {
    const key = `${candidate.signal}:${candidate.intent}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

function rule(source: string, signal: string, intent: string, delta: number, confidence: number): IntentRule {
  return {
    source,
    signal,
    intent,
    delta,
    confidence,
  };
}

function getTopIntent(scores: Record<string, number>): string | null {
  return (
    Object.entries(scores)
      .sort(([, a], [, b]) => b - a)
      .map(([intent]) => intent)[0] ?? null
  );
}

function clamp(value: number): number {
  return Math.max(0, Math.min(1, Number(value.toFixed(4))));
}
